import findLastIndex from './findLastIndex';
import getWords from './getWords';
import type { TruncationResult } from './truncateText';

function findWordEndIndex(text: string, words: string[], wordIndex: number): number {
  let charIndex = 0;
  for (let i = 0; i <= wordIndex; i++) {
    const wordPos = text.indexOf(words[i], charIndex);
    if (wordPos === -1) {
      return charIndex;
    }
    charIndex = wordPos + words[i].length;
  }

  // Only include trailing ASCII punctuation (.,;:!?'")-] etc.)
  // Using [^\s\w] would incorrectly match CJK characters since \w only matches ASCII
  const trailingPunctuation = text.slice(charIndex).match(/^[.,;:!?'")\]\-}]*/);
  if (trailingPunctuation) {
    charIndex += trailingPunctuation[0].length;
  }

  return charIndex;
}

const SELF_CLOSING_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

const MEDIA_TAGS = new Set(['img', 'svg', 'video', 'audio', 'canvas', 'picture', 'iframe', 'object']);

interface TagInfo {
  name: string;
  full: string;
}

function extractTextContent(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function parseTag(tagStr: string): { name: string; isClosing: boolean; isSelfClosing: boolean; } | null {
  const match = tagStr.match(/^<\s*(\/?)(\w+)/);
  if (!match) {
    return null;
  }
  const isClosing = match[1] === '/';
  const name = match[2].toLowerCase();
  const isSelfClosing = SELF_CLOSING_TAGS.has(name) || tagStr.endsWith('/>');
  return { name, isClosing, isSelfClosing };
}

export function truncateHtml(
  html: string,
  maxWords: number,
  language = 'unknown',
): TruncationResult {
  if (!html.trim()) {
    return { text: html, wasTruncated: false, originalWordCount: 0, truncatedWordCount: 0 };
  }

  const textContent = extractTextContent(html);
  const allWords = getWords(textContent, language);
  const originalWordCount = allWords.length;

  if (originalWordCount <= maxWords) {
    return { text: html, wasTruncated: false, originalWordCount, truncatedWordCount: originalWordCount };
  }

  const result: string[] = [];
  const openTags: TagInfo[] = [];
  let wordCount = 0;
  let i = 0;
  let hitLimit = false;

  while (i < html.length) {
    if (hitLimit) {
      break;
    }

    if (html[i] === '<') {
      const tagEnd = html.indexOf('>', i);
      if (tagEnd === -1) {
        result.push(html.slice(i));
        break;
      }

      const tagStr = html.slice(i, tagEnd + 1);
      const tagInfo = parseTag(tagStr);

      if (tagInfo) {
        if (tagInfo.isClosing) {
          const lastOpenIdx = findLastIndex(openTags, (tag: TagInfo) => tag.name === tagInfo.name);
          if (lastOpenIdx >= 0) {
            openTags.splice(lastOpenIdx, 1);
          }
          result.push(tagStr);
        } else if (!tagInfo.isSelfClosing) {
          openTags.push({ name: tagInfo.name, full: tagStr });
          result.push(tagStr);
        } else {
          result.push(tagStr);
        }
      } else {
        result.push(tagStr);
      }

      i = tagEnd + 1;
    } else {
      let textEnd = html.indexOf('<', i);
      if (textEnd === -1) {
        textEnd = html.length;
      }

      const textSegment = html.slice(i, textEnd);
      const segmentWords = getWords(textSegment, language);

      if (wordCount + segmentWords.length <= maxWords) {
        result.push(textSegment);
        wordCount += segmentWords.length;
        i = textEnd;
      } else {
        const remainingWords = maxWords - wordCount;
        if (remainingWords > 0 && segmentWords.length > 0) {
          const endIndex = findWordEndIndex(textSegment, segmentWords, remainingWords - 1);
          result.push(textSegment.slice(0, endIndex));
          wordCount = maxWords;
        }
        hitLimit = true;
      }
    }
  }

  for (let j = openTags.length - 1; j >= 0; j--) {
    result.push(`</${openTags[j].name}>`);
  }

  let finalHtml = result.join('');
  finalHtml = cleanupEmptyElements(finalHtml);

  return {
    text: finalHtml,
    wasTruncated: true,
    originalWordCount,
    truncatedWordCount: wordCount,
  };
}


function isEmptyContent(content: string): boolean {
  const withoutEntities = content
    .replace(/&[a-zA-Z]+;/g, '')
    .replace(/&#x?[0-9a-fA-F]+;/g, '');
  return withoutEntities.trim() === '';
}

function cleanupEmptyElements(html: string): string {
  let previous = '';
  let current = html;

  while (previous !== current) {
    previous = current;
    current = current.replace(/<(\w+)(\s[^>]*)?>([^<]*)<\/\1>/gi, (match, tagName, _attrs, content) => {
      if (MEDIA_TAGS.has(tagName.toLowerCase())) {
        return match;
      }
      if (isEmptyContent(content)) {
        return '';
      }
      return match;
    });
  }

  return current.trim();
}
